/**
 * Persists FileSystemDirectoryHandle in IndexedDB keyed by drawing (detail_revision).
 * Each settings file can have its own save folder.
 */

const DB_NAME = 'QualityDatumDownloadFolder';
const DB_VERSION = 3;
const STORE_NAME = 'handles';
const DEFAULT_KEY = 'default';

function sanitizeKey(s: string): string {
    return (s || '').replace(/[^a-zA-Z0-9а-яА-ЯёЁ\-_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'unknown';
}

export function buildFolderKey(detail?: string, _revision?: string): string {
    const d = sanitizeKey(detail || '');
    if (!d) return DEFAULT_KEY;
    return d;
}

/** IndexedDB key for editor "PDF with embedded settings" saves (separate from report folder). */
export function buildSettingsPdfFolderKey(detail?: string, revision?: string): string {
    return `${buildFolderKey(detail, revision)}:settingsPdf`;
}

export async function saveDownloadDirHandle(handle: FileSystemDirectoryHandle, key?: string): Promise<void> {
    const k = key || DEFAULT_KEY;
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(handle, k);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function clearDownloadDirHandle(key?: string): Promise<void> {
    const k = key || DEFAULT_KEY;
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).delete(k);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function getDownloadDirHandle(key?: string): Promise<FileSystemDirectoryHandle | null> {
    const k = key || DEFAULT_KEY;
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(k);
        req.onsuccess = () => {
            let result = req.result ?? null;
            if (!result && k === DEFAULT_KEY) {
                const legacyReq = store.get('downloadDirHandle');
                legacyReq.onsuccess = () => resolve(legacyReq.result ?? null);
                legacyReq.onerror = () => reject(legacyReq.error);
            } else {
                resolve(result);
            }
        };
        req.onerror = () => reject(req.error);
    });
}

function openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve(req.result);
        req.onupgradeneeded = (e) => {
            const db = (e.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
    });
}
